// =====================================================================
// /admin/period-locks - Faza 3.3 (D-004)
// =====================================================================
// Pagina admin pentru cloture mensuelle (Luca FINANCE locheaza, Silviu
// ADMIN deblocheaza cu raison + audit). Layout single page:
//   - Info card galben sus
//   - Form lock (modal): input month + buton "Luna trecuta" + raison
//   - Tabel verrouillages actifs
//   - Section collapsible "Historique" (lazy load)
//
// Permisiuni:
//   - timesheets.lock_period.all   -> vede + locheaza (FINANCE + ADMIN)
//   - timesheets.unlock_period.all -> deblocheaza (ADMIN)
// =====================================================================

import { useState, useEffect, useMemo } from 'react';
import { periodLocks as periodLocksApi, overtime as overtimeApi } from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';

// ---------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------

const MONTH_NAMES_FR = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];

// ---------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------

function thisMonthYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function previousMonthYmd() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** "2026-04" -> "avril 2026" */
function formatPeriodFR(period) {
  if (!period || !period.match(/^\d{4}-\d{2}$/)) return period || '';
  const [y, m] = period.split('-').map(Number);
  return `${MONTH_NAMES_FR[m - 1]} ${y}`;
}

function formatDateTimeFR(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('fr-BE', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function extractError(e) {
  return e?.response?.data?.message
    || e?.response?.data?.error
    || e?.message
    || 'Erreur inconnue';
}

// ---------------------------------------------------------------------
// Modal: Lock period
// ---------------------------------------------------------------------

function LockModal({ onClose, onSave }) {
  const [period, setPeriod] = useState(previousMonthYmd());  // default luna trecuta
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function handleSave() {
    setError(null);
    if (!period || !period.match(/^\d{4}-\d{2}$/)) {
      setError('Période invalide (format YYYY-MM attendu).');
      return;
    }
    setSubmitting(true);
    try {
      await onSave({ period, reason: reason.trim() || null });
      onClose();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSubmitting(false);
    }
  }

  function setPrev() { setPeriod(previousMonthYmd()); }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeaderStyle}>
          <h3 style={{ margin: 0, fontSize: 18 }}>Verrouiller une période</h3>
          <button onClick={onClose} style={btnIconStyle} aria-label="Fermer">×</button>
        </div>

        <div style={warningCardStyle}>
          <strong>⚠ Attention :</strong> le verrouillage bloque toutes les modifications
          (TimesheetEntry et Présence) pour la période sélectionnée. Seul un ADMIN
          peut déverrouiller.
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Période</label>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              style={{ ...selectStyle, flex: 1 }}
              max={thisMonthYmd()}
            />
            <button
              type="button"
              onClick={setPrev}
              style={btnSecondaryStyle}
              title="Définir comme la période du mois précédent"
            >
              Mois précédent
            </button>
          </div>
          <small style={{ color: '#64748b', fontSize: 12 }}>
            Sélectionnée : <strong>{formatPeriodFR(period)}</strong>
          </small>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Motif (facultatif)</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            style={{ ...selectStyle, fontFamily: 'inherit', resize: 'vertical' }}
            placeholder="Ex : Clôture mensuelle pour facturation"
            maxLength={500}
          />
          <small style={{ color: '#64748b', fontSize: 12 }}>
            {reason.length} / 500
          </small>
        </div>

        {error && (
          <div style={errorBoxStyle}>{error}</div>
        )}

        <div style={modalFooterStyle}>
          <button onClick={onClose} disabled={submitting} style={btnSecondaryStyle}>
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={submitting || !period}
            style={btnPrimaryStyle}
          >
            {submitting ? 'Verrouillage…' : '🔒 Verrouiller'}
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------
// Modal: Trigger overtime decisions (Day 6 / OT_DEC_V1)
// ---------------------------------------------------------------------

function TriggerDecisionsModal({ onClose, onTrigger }) {
  const [mode, setMode] = useState('single'); // 'single' | 'range'
  const [month, setMonth] = useState(previousMonthYmd());
  const [fromMonth, setFromMonth] = useState(previousMonthYmd());
  const [toMonth, setToMonth] = useState(previousMonthYmd());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  async function handleSubmit() {
    setError(null);
    setResult(null);
    const body = mode === 'single' ? { month } : { fromMonth, toMonth };
    setSubmitting(true);
    try {
      const r = await onTrigger(body);
      setResult(r);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeaderStyle}>
          <h3 style={{ margin: 0, fontSize: 18 }}>Déclencher décisions HS</h3>
          <button onClick={onClose} style={btnIconStyle} aria-label="Fermer">×</button>
        </div>

        <div style={{ ...infoCardStyle, marginBottom: 14, background: '#eff6ff', borderColor: '#bfdbfe' }}>
          <strong>💡 À propos :</strong> cette action crée des décisions <em>en attente</em> pour
          tous les opérateurs ayant des heures supplémentaires non réglées sur la période sélectionnée.
          Chaque opérateur pourra ensuite choisir comment répartir son solde (payer, déduire, reporter).
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Mode</label>
          <div style={{ display: 'flex', gap: 16 }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" checked={mode === 'single'} onChange={() => setMode('single')} />
              Une seule mois
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" checked={mode === 'range'} onChange={() => setMode('range')} />
              Plage de mois
            </label>
          </div>
        </div>

        {mode === 'single' ? (
          <div style={{ marginBottom: 14 }}>
            <label style={labelStyle}>Mois</label>
            <input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              style={selectStyle}
              max={thisMonthYmd()}
            />
            <small style={{ color: '#64748b', fontSize: 12 }}>
              Sélectionné : <strong>{formatPeriodFR(month)}</strong>
            </small>
          </div>
        ) : (
          <div style={{ marginBottom: 14, display: 'flex', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Du mois</label>
              <input type="month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} style={selectStyle} max={thisMonthYmd()} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Au mois</label>
              <input type="month" value={toMonth} onChange={(e) => setToMonth(e.target.value)} style={selectStyle} max={thisMonthYmd()} />
            </div>
          </div>
        )}

        {error && <div style={errorBoxStyle}>{error}</div>}

        {result && (
          <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: 12, marginBottom: 14, fontSize: 13 }}>
            <strong>✓ Résultat :</strong>
            <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
              <li>{result.triggered?.length || 0} décision(s) créée(s)</li>
              <li>{result.skipped?.length || 0} ignorée(s) (déjà existantes)</li>
              <li>{result.monthsProcessed || 0} mois traités</li>
            </ul>
            {result.triggered?.length > 0 && (
              <details style={{ marginTop: 8 }}>
                <summary style={{ cursor: 'pointer', fontSize: 12, color: '#16a34a' }}>Voir les détails</summary>
                <ul style={{ margin: '6px 0 0 18px', padding: 0, fontSize: 12 }}>
                  {result.triggered.map((t, i) => (
                    <li key={i}>{t.userId} — {t.month} — solde {t.balance > 0 ? '+' : ''}{t.balance}h ({t.entriesLinked} entrée(s))</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        <div style={modalFooterStyle}>
          <button onClick={onClose} disabled={submitting} style={btnSecondaryStyle}>
            {result ? 'Fermer' : 'Annuler'}
          </button>
          {!result && (
            <button onClick={handleSubmit} disabled={submitting} style={btnPrimaryStyle}>
              {submitting ? 'Déclenchement…' : '⚡ Déclencher'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Modal: Unlock period
// ---------------------------------------------------------------------

function UnlockModal({ lock, onClose, onSave }) {
  const [unlockReason, setUnlockReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const valid = unlockReason.trim().length >= 3;

  async function handleSave() {
    setError(null);
    if (!valid) {
      setError('Motif obligatoire (minimum 3 caractères).');
      return;
    }
    setSubmitting(true);
    try {
      await onSave({ period: lock.period, unlockReason: unlockReason.trim() });
      onClose();
    } catch (e) {
      setError(extractError(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={modalHeaderStyle}>
          <h3 style={{ margin: 0, fontSize: 18 }}>Déverrouiller {formatPeriodFR(lock.period)}</h3>
          <button onClick={onClose} style={btnIconStyle} aria-label="Fermer">×</button>
        </div>

        <div style={warningCardRedStyle}>
          <strong>⚠ Action sensible :</strong> le déverrouillage redonne accès aux
          modifications sur TimesheetEntry et Présence pour cette période.
          Le motif sera enregistré dans l'audit.
        </div>

        <div style={detailsBoxStyle}>
          <div><strong>Période :</strong> {formatPeriodFR(lock.period)}</div>
          <div><strong>Verrouillée le :</strong> {formatDateTimeFR(lock.lockedAt)}</div>
          <div><strong>Par :</strong> {lock.lockedBy?.name || '?'}</div>
          {lock.reason && <div><strong>Motif initial :</strong> {lock.reason}</div>}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Motif de déverrouillage *</label>
          <textarea
            value={unlockReason}
            onChange={(e) => setUnlockReason(e.target.value)}
            rows={3}
            style={{ ...selectStyle, fontFamily: 'inherit', resize: 'vertical' }}
            placeholder="Expliquez pourquoi cette période doit être déverrouillée…"
            maxLength={500}
            autoFocus
          />
          <small style={{ color: valid ? '#64748b' : '#dc2626', fontSize: 12 }}>
            {unlockReason.length} / 500 (minimum 3)
          </small>
        </div>

        {error && (
          <div style={errorBoxStyle}>{error}</div>
        )}

        <div style={modalFooterStyle}>
          <button onClick={onClose} disabled={submitting} style={btnSecondaryStyle}>
            Annuler
          </button>
          <button
            onClick={handleSave}
            disabled={submitting || !valid}
            style={btnDangerStyle}
          >
            {submitting ? 'Déverrouillage…' : '🔓 Déverrouiller'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------
// Toast simplu (auto-hide 4s)
// ---------------------------------------------------------------------

function Toast({ message, type = 'success', onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);

  const bg = type === 'error' ? '#dc2626' : '#16a34a';
  return (
    <div style={{
      position: 'fixed', top: 16, right: 16, zIndex: 1100,
      background: bg, color: 'white', padding: '10px 16px',
      borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      display: 'flex', alignItems: 'center', gap: 12,
      maxWidth: 400,
    }}>
      <span>{message}</span>
      <button
        onClick={onClose}
        style={{
          background: 'transparent', border: 'none', color: 'white',
          fontSize: 18, cursor: 'pointer', padding: 0, lineHeight: 1,
        }}
      >×</button>
    </div>
  );
}

// ---------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------

export default function PeriodLocks() {
  const { user, can } = useAuth();
  const [activeLocks, setActiveLocks] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLockModal, setShowLockModal] = useState(false);
  const [showTriggerModal, setShowTriggerModal] = useState(false);
  const [unlockTarget, setUnlockTarget] = useState(null);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [toast, setToast] = useState(null);  // { message, type }

  const canLock = can?.('timesheets.lock_period.all');
  const canUnlock = can?.('timesheets.unlock_period.all');

  async function loadActive() {
    setLoading(true);
    setError(null);
    try {
      const data = await periodLocksApi.list({ activeOnly: true });
      setActiveLocks(data || []);
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }

  async function loadHistory() {
    setHistoryLoading(true);
    try {
      const data = await periodLocksApi.list({ activeOnly: false, limit: 200 });
      // Filtreaza istorice (isActive=false) - active vor fi deja in lista de sus
      setHistory((data || []).filter((l) => !l.isActive));
    } catch (e) {
      setToast({ message: extractError(e), type: 'error' });
    } finally {
      setHistoryLoading(false);
    }
  }

  useEffect(() => {
    loadActive();
  }, []);

  function toggleHistory() {
    if (!historyVisible && history.length === 0) {
      loadHistory();
    }
    setHistoryVisible(!historyVisible);
  }

  async function handleLock({ period, reason }) {
    const created = await periodLocksApi.create({ period, reason });
    setToast({
      message: `Période ${formatPeriodFR(created.period)} verrouillée`,
      type: 'success',
    });
    await loadActive();
    if (historyVisible) await loadHistory();
  }

  async function handleUnlock({ period, unlockReason }) {
    await periodLocksApi.unlock(period, unlockReason);
    setToast({
      message: `Période ${formatPeriodFR(period)} déverrouillée`,
      type: 'success',
    });
    await loadActive();
    if (historyVisible) await loadHistory();
  }

  async function handleTrigger(body) {
    return await overtimeApi.triggerDecisions(body);
  }

  if (!user) return <div style={{ padding: 24 }}>Authentification requise.</div>;

  if (!canLock) {
    return (
      <div style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
        <h1 style={{ margin: '0 0 16px 0', fontSize: 24 }}>Verrouillage des périodes</h1>
        <div style={errorBoxStyle}>
          Accès refusé. Cette page est réservée au rôle FINANCE et ADMIN.
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
      }}>
        <h1 style={{ margin: 0, fontSize: 24 }}>Verrouillage des périodes</h1>
        <span style={countBadgeStyle}>{activeLocks.length}</span>
      </div>

      {/* Info card */}
      <div style={infoCardStyle}>
        <strong>💡 À propos :</strong> verrouiller une période bloque toutes les
        modifications sur les feuilles de temps (TimesheetEntry) et les présences
        (Attendance) pour le mois sélectionné. Cela permet de figer les données
        avant la facturation. Seul un ADMIN peut déverrouiller, avec motif obligatoire.
      </div>

      {/* Action buttons */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button
          onClick={() => setShowLockModal(true)}
          style={btnPrimaryStyle}
        >
          + Verrouiller une période
        </button>
        <button
          onClick={() => setShowTriggerModal(true)}
          style={{ ...btnPrimaryStyle, background: '#7c3aed' }}
          title="Créer des décisions en attente pour tous les opérateurs avec des heures sup non réglées"
        >
          ⚡ Déclencher décisions HS
        </button>
      </div>

      {/* Errors */}
      {error && (
        <div style={errorBoxStyle}>
          {error}
          <button
            onClick={() => setError(null)}
            style={{ marginLeft: 12, ...btnIconSmallStyle, color: '#991b1b' }}
          >×</button>
        </div>
      )}

      {/* Active locks table */}
      <div style={sectionStyle}>
        <h3 style={sectionHeaderStyle}>Verrouillages actifs</h3>

        {loading ? (
          <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>
            Chargement…
          </div>
        ) : activeLocks.length === 0 ? (
          <div style={emptyStateStyle}>
            🔓 Aucune période verrouillée actuellement.
          </div>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr style={tableHeaderRowStyle}>
                <th style={thStyle}>Période</th>
                <th style={thStyle}>Verrouillée le</th>
                <th style={thStyle}>Par</th>
                <th style={thStyle}>Motif</th>
                <th style={thRightStyle}>Action</th>
              </tr>
            </thead>
            <tbody>
              {activeLocks.map((lock) => (
                <tr key={lock.id} style={tableRowStyle}>
                  <td style={tdStyle}>
                    <span style={periodBadgeStyle}>🔒 {formatPeriodFR(lock.period)}</span>
                  </td>
                  <td style={tdStyle}>{formatDateTimeFR(lock.lockedAt)}</td>
                  <td style={tdStyle}>{lock.lockedBy?.name || '—'}</td>
                  <td style={{ ...tdStyle, color: '#64748b', fontSize: 12 }}>
                    {lock.reason || <em>—</em>}
                  </td>
                  <td style={tdRightStyle}>
                    {canUnlock ? (
                      <button
                        onClick={() => setUnlockTarget(lock)}
                        style={btnDangerSmallStyle}
                        title="Déverrouiller"
                      >
                        🔓 Déverrouiller
                      </button>
                    ) : (
                      <span
                        style={{ color: '#94a3b8', fontSize: 12 }}
                        title="Réservé ADMIN"
                      >
                        Réservé ADMIN
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Collapsible history */}
      <div style={sectionStyle}>
        <button
          onClick={toggleHistory}
          style={collapseToggleStyle}
        >
          {historyVisible ? '▼' : '▶'} Historique
          {history.length > 0 && (
            <span style={{ ...countBadgeStyle, background: '#94a3b8', marginLeft: 8 }}>
              {history.length}
            </span>
          )}
        </button>

        {historyVisible && (
          <div style={{ marginTop: 12 }}>
            {historyLoading ? (
              <div style={{ padding: 24, textAlign: 'center', color: '#64748b' }}>
                Chargement…
              </div>
            ) : history.length === 0 ? (
              <div style={emptyStateStyle}>
                Aucun historique de déverrouillage.
              </div>
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr style={tableHeaderRowStyle}>
                    <th style={thStyle}>Période</th>
                    <th style={thStyle}>Verrouillée</th>
                    <th style={thStyle}>Par</th>
                    <th style={thStyle}>Déverrouillée</th>
                    <th style={thStyle}>Par</th>
                    <th style={thStyle}>Motif déver.</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id} style={tableRowStyle}>
                      <td style={tdStyle}>
                        <span style={{ ...periodBadgeStyle, background: '#f1f5f9', color: '#475569' }}>
                          {formatPeriodFR(h.period)}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontSize: 12, color: '#64748b' }}>
                        {formatDateTimeFR(h.lockedAt)}
                      </td>
                      <td style={{ ...tdStyle, fontSize: 12 }}>{h.lockedBy?.name || '—'}</td>
                      <td style={{ ...tdStyle, fontSize: 12, color: '#64748b' }}>
                        {formatDateTimeFR(h.unlockedAt)}
                      </td>
                      <td style={{ ...tdStyle, fontSize: 12 }}>{h.unlockedBy?.name || '—'}</td>
                      <td style={{ ...tdStyle, fontSize: 12, color: '#64748b' }}>
                        {h.unlockReason || <em>—</em>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showLockModal && (
        <LockModal
          onClose={() => setShowLockModal(false)}
          onSave={handleLock}
        />
      )}

      {showTriggerModal && (
        <TriggerDecisionsModal
          onClose={() => setShowTriggerModal(false)}
          onTrigger={handleTrigger}
        />
      )}
      {unlockTarget && (
        <UnlockModal
          lock={unlockTarget}
          onClose={() => setUnlockTarget(null)}
          onSave={handleUnlock}
        />
      )}

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------

const overlayStyle = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(15, 23, 42, 0.5)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};

const modalStyle = {
  background: 'white', borderRadius: 8, padding: 24,
  width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto',
  boxShadow: '0 20px 50px rgba(0, 0, 0, 0.25)',
};

const modalHeaderStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  marginBottom: 16,
};

const modalFooterStyle = {
  display: 'flex', gap: 8, justifyContent: 'flex-end',
};

const labelStyle = {
  display: 'block', marginBottom: 4, fontSize: 13,
  fontWeight: 500, color: '#475569',
};

const selectStyle = {
  width: '100%', padding: '6px 8px', fontSize: 13,
  border: '1px solid #cbd5e1', borderRadius: 4, background: 'white',
  boxSizing: 'border-box',
};

const errorBoxStyle = {
  background: '#fee2e2', color: '#991b1b', padding: 10,
  borderRadius: 4, fontSize: 13, marginBottom: 12,
  display: 'flex', alignItems: 'center',
};

const warningCardStyle = {
  background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e',
  padding: 10, borderRadius: 6, fontSize: 13, marginBottom: 14,
};

const warningCardRedStyle = {
  background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b',
  padding: 10, borderRadius: 6, fontSize: 13, marginBottom: 14,
};

const detailsBoxStyle = {
  background: '#f1f5f9', padding: 10, borderRadius: 4, marginBottom: 14,
  fontSize: 13, color: '#334155', lineHeight: 1.6,
};

const infoCardStyle = {
  background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e',
  padding: 12, borderRadius: 6, fontSize: 13, marginBottom: 16,
  lineHeight: 1.5,
};

const sectionStyle = {
  marginBottom: 24,
};

const sectionHeaderStyle = {
  margin: '0 0 12px 0', fontSize: 16, color: '#475569',
};

const tableStyle = {
  borderCollapse: 'collapse', width: '100%',
  background: 'white', border: '1px solid #e2e8f0', borderRadius: 6,
};

const tableHeaderRowStyle = {
  background: '#f8fafc',
};

const tableRowStyle = {
  borderTop: '1px solid #f1f5f9',
};

const thStyle = {
  padding: '8px 12px', textAlign: 'left', fontSize: 12,
  fontWeight: 600, color: '#475569', borderBottom: '2px solid #e2e8f0',
};

const thRightStyle = { ...thStyle, textAlign: 'right' };

const tdStyle = {
  padding: '10px 12px', fontSize: 13, color: '#334155',
};

const tdRightStyle = { ...tdStyle, textAlign: 'right' };

const periodBadgeStyle = {
  display: 'inline-block', background: '#fee2e2', color: '#991b1b',
  padding: '4px 10px', borderRadius: 4, fontSize: 13, fontWeight: 600,
};

const countBadgeStyle = {
  background: '#1e40af', color: 'white', borderRadius: 12,
  padding: '2px 10px', fontSize: 13, fontWeight: 600,
};

const emptyStateStyle = {
  padding: 32, textAlign: 'center', color: '#64748b',
  background: '#f8fafc', borderRadius: 6, fontSize: 14,
};

const collapseToggleStyle = {
  background: 'transparent', border: 'none', cursor: 'pointer',
  fontSize: 16, fontWeight: 600, color: '#475569', padding: 0,
  display: 'flex', alignItems: 'center',
};

const btnPrimaryStyle = {
  background: '#1e40af', color: 'white', border: 'none',
  padding: '8px 16px', borderRadius: 4, cursor: 'pointer',
  fontSize: 13, fontWeight: 600,
};

const btnSecondaryStyle = {
  background: 'white', color: '#475569', border: '1px solid #cbd5e1',
  padding: '6px 12px', borderRadius: 4, cursor: 'pointer',
  fontSize: 13, fontWeight: 500,
};

const btnDangerStyle = {
  background: '#dc2626', color: 'white', border: 'none',
  padding: '8px 16px', borderRadius: 4, cursor: 'pointer',
  fontSize: 13, fontWeight: 600,
};

const btnDangerSmallStyle = {
  background: '#dc2626', color: 'white', border: 'none',
  padding: '4px 10px', borderRadius: 4, cursor: 'pointer',
  fontSize: 12, fontWeight: 500,
};

const btnIconStyle = {
  background: 'transparent', border: '1px solid #cbd5e1',
  width: 32, height: 32, borderRadius: 4, cursor: 'pointer',
  fontSize: 14, color: '#475569', display: 'inline-flex',
  alignItems: 'center', justifyContent: 'center',
};

const btnIconSmallStyle = {
  background: 'transparent', border: 'none',
  cursor: 'pointer', fontSize: 14, padding: '2px 6px',
};
